import { useEffect, useRef, type KeyboardEvent } from 'react';
import type { FaceState } from '../pal/face';
import type { NowPlayingTrack } from '../native/music-listener-native';
import type { FaceBaseProps } from './FacePropAdapter';
import type { DriverStatus } from './hooks/useDriveFaceState';
import type { VoiceSessionState } from './hooks/useVoiceSession';
import { computeSmoothness, type TripSnapshot } from '../drive/trip-session';
import { AmbientMode } from '../pal/ambient';
import { PalFaceCartoon } from './PalFaceCartoon';
import { NowPlayingChip } from './NowPlayingChip';
import { LandscapeDriveCockpitHalo } from './LandscapeDriveCockpitHalo';
import { VoiceMicFAB } from './VoiceMicFAB';

interface LandscapeDriveDisplayProps {
    snapshot: TripSnapshot;
    status: DriverStatus;
    faceState: FaceState;
    faceBaseProps: FaceBaseProps;
    ambient?: AmbientMode;
    nowPlaying?: NowPlayingTrack | null;
    voiceControls?: {
        visible: boolean;
        state: VoiceSessionState;
        onPress: () => void;
    };
    onEndTrip: () => void;
    onOpenMap: () => void;
}

export function shouldShowLandscapeCockpit(snapshot: Pick<TripSnapshot, 'state'>, isLandscape: boolean): boolean {
    return snapshot.state === 'driving' && isLandscape;
}

export function LandscapeDriveDisplay({
    snapshot,
    status,
    faceState,
    faceBaseProps,
    ambient = AmbientMode.NONE,
    nowPlaying,
    voiceControls,
    onEndTrip,
    onOpenMap,
}: LandscapeDriveDisplayProps) {
    const speed = snapshot.last_speed_kmh ?? 0;
    const rpm = snapshot.last_rpm ?? 0;
    const smoothness = Math.round(computeSmoothness(snapshot) * 100);
    const connected = status === 'connected';
    const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const voiceVisible = voiceControls?.visible === true;

    function clearFacePressTimer() {
        if (longPressTimer.current === null) return;
        clearTimeout(longPressTimer.current);
        longPressTimer.current = null;
    }

    function startFaceLongPress() {
        if (!voiceVisible) return;
        clearFacePressTimer();
        longPressTimer.current = setTimeout(() => {
            longPressTimer.current = null;
            voiceControls?.onPress();
        }, 550);
    }

    function handleFaceKeyDown(e: KeyboardEvent<HTMLDivElement>) {
        if (!voiceVisible) return;
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();
        voiceControls?.onPress();
    }

    useEffect(() => clearFacePressTimer, []);

    return (
        <div
            role="region"
            aria-label="Drive cockpit"
            className="fixed inset-0 z-40 flex bg-cockpit text-cockpit-text"
            style={{
                paddingTop: 'max(env(safe-area-inset-top), 12px)',
                paddingRight: 'max(env(safe-area-inset-right), 14px)',
                paddingBottom: 'max(env(safe-area-inset-bottom), 12px)',
                paddingLeft: 'max(env(safe-area-inset-left), 14px)',
            }}
        >
            <div className="grid h-full w-full grid-cols-[1fr_220px] gap-4">
                <main className="flex min-w-0 flex-col items-center justify-center">
                    <div
                        className="relative w-full max-w-[min(74vw,760px)]"
                        role={voiceVisible ? 'button' : undefined}
                        tabIndex={voiceVisible ? 0 : undefined}
                        aria-label={voiceVisible ? 'Press and hold to talk to Moji' : undefined}
                        data-voice-face-target={voiceVisible ? 'true' : 'false'}
                        onPointerDown={startFaceLongPress}
                        onPointerUp={clearFacePressTimer}
                        onPointerLeave={clearFacePressTimer}
                        onPointerCancel={clearFacePressTimer}
                        onContextMenu={(e) => {
                            if (voiceVisible) e.preventDefault();
                        }}
                        onKeyDown={handleFaceKeyDown}
                    >
                        {voiceControls && <LandscapeDriveCockpitHalo state={voiceControls.state} />}
                        <PalFaceCartoon
                            state={faceState}
                            form={faceBaseProps.form}
                            marks={faceBaseProps.marks}
                            accessory={faceBaseProps.accessory}
                            ambient={ambient}
                            active={true}
                            scale={5}
                            maxWidth="100%"
                            className="rounded-xl shadow-[0_0_28px_rgb(77_141_255_/_0.22)]"
                        />
                    </div>
                    <div className="mt-3 flex items-center gap-3 text-xs uppercase tracking-wide text-cockpit-muted">
                        <span className={`h-2 w-2 rounded-full ${connected ? 'bg-success' : 'bg-danger'}`} aria-hidden />
                        <span>{connected ? 'Connected' : 'Signal unstable'}</span>
                        <span>Landscape cockpit</span>
                    </div>
                </main>

                <aside className="flex min-w-0 flex-col justify-between">
                    <div className="grid grid-cols-2 gap-2">
                        <CockpitStat label="Speed" value={`${speed.toFixed(0)}`} unit="km/h" strong />
                        <CockpitStat label="RPM" value={rpm > 0 ? Math.round(rpm).toLocaleString() : '-'} />
                        <CockpitStat label="Smooth" value={`${smoothness}`} unit="%" />
                        <CockpitStat label="Brakes" value={String(snapshot.hard_brakes)} />
                        <CockpitStat label="Moving" value={formatDuration(snapshot.moving_s)} />
                        <CockpitStat label="Distance" value={snapshot.distance_km.toFixed(1)} unit="km" />
                    </div>

                    <div className="space-y-2">
                        <NowPlayingChip track={nowPlaying ?? null} tone="dark" />
                        <div className="rounded-lg border border-cockpit-panel/10 bg-cockpit-panel/[0.04] px-3 py-2 text-xs text-cockpit-muted">
                            {snapshot.route.length > 0
                                ? `${snapshot.route.length} route points live`
                                : 'Route appears after GPS locks'}
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            <button type="button" className="btn-secondary bg-cockpit-panel/10 text-cockpit-text" onClick={onOpenMap}>
                                Map
                            </button>
                            <button type="button" className="btn-primary" onClick={onEndTrip}>
                                End
                            </button>
                        </div>
                    </div>
                </aside>
            </div>
            {voiceControls && (
                <VoiceMicFAB
                    visible={voiceControls.visible}
                    state={voiceControls.state}
                    onPress={voiceControls.onPress}
                />
            )}
        </div>
    );
}

function CockpitStat({ label, value, unit, strong = false }: {
    label: string;
    value: string;
    unit?: string;
    strong?: boolean;
}) {
    return (
        <div className="rounded-lg border border-cockpit-panel/10 bg-cockpit-panel/[0.06] p-2">
            <div className="text-[11px] text-cockpit-muted">{label}</div>
            <div className={strong ? 'text-3xl font-semibold leading-none' : 'text-lg font-semibold leading-tight'}>
                {value}
                {unit && <span className="ml-1 text-xs font-normal text-cockpit-muted">{unit}</span>}
            </div>
        </div>
    );
}

function formatDuration(s: number): string {
    const mins = Math.floor(s / 60);
    const secs = Math.floor(s % 60);
    if (mins === 0) return `${secs}s`;
    if (mins < 60) return `${mins}m`;
    const hours = Math.floor(mins / 60);
    return `${hours}h ${mins % 60}m`;
}
